// src/components/layout/BackHomeButton.tsx
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type BackHomeButtonProps = {
  className?: string;
  variant?: 'floating' | 'inline';
  forceShow?: boolean;
};

const HOME_PATHS = ['/', '/home', '/accueil'];

const BackHomeButton: React.FC<BackHomeButtonProps> = ({
  className = '',
  variant = 'inline',
  forceShow = false,
}) => {
  const location = useLocation();
  const { t } = useTranslation();

  const isHomePage = HOME_PATHS.includes(location.pathname);

  if (isHomePage && !forceShow) {
    return null;
  }

  const label =
    t('navigation.backHome', {
      defaultValue: "Retour à l’accueil",
    }) || "Retour à l’accueil";

  const shortHomeLabel = t('navigation.homeShort', {
    defaultValue: 'Accueil',
  });

  const baseClasses =
    'group inline-flex h-10 w-10 items-center justify-center gap-1.5 rounded-full border border-slate-200/80 bg-white/90 p-0 text-xs font-semibold text-slate-700 shadow-sm backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 lg:h-9 lg:w-auto lg:rounded-xl lg:px-2.5 lg:py-1.5';

  /**
   * Position flottante pensée pour ne pas entrer en conflit avec :
   * - le footer fixe existant ;
   * - le panier ;
   * - l’assistant Forfeo ;
   * - les boutons mobiles en bas d’écran.
   *
   * Sur mobile, le bouton est placé au-dessus du footer.
   * Sur desktop, il reste discret à gauche.
   */
  const floatingClasses =
    'fixed left-3 bottom-[calc(env(safe-area-inset-bottom)+4.25rem)] z-[9994] lg:left-6 lg:bottom-6';

  const inlineClasses = 'my-4';

  return (
    <Link
      to="/"
      aria-label={label}
      className={[
        baseClasses,
        variant === 'floating' ? floatingClasses : inlineClasses,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span
        aria-hidden="true"
        className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-sm transition-colors duration-200 group-hover:bg-orange-100 lg:h-6 lg:w-6"
      >
        ←
      </span>

      <span className="hidden lg:inline">{shortHomeLabel}</span>

      <span className="sr-only lg:hidden">
        {t('common.back', {
          defaultValue: 'Retour',
        })}
      </span>
    </Link>
  );
};

export default BackHomeButton;
